import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertCircle } from "react-icons/tb";
import { SettingRow, TextInput, Section } from "./SettingsControls";
import { normalizeIban, validateIban } from "src/helpers/sanitizeInput";

export type SepaFields = { iban: string; bic: string; accountHolder: string };

interface SepaSettingsProps {
    sepa: SepaFields;
    onChange: (s: SepaFields) => void;
    onIbanError?: (hasError: boolean) => void;
    description?: string;
}

export function SepaSettings({ sepa, onChange, onIbanError, description }: SepaSettingsProps) {
    const { t } = useTranslation();
    const [ibanError, setIbanError] = useState<string | null>(null);

    const handleIbanBlur = (raw: string) => {
        const normalized = normalizeIban(raw);
        onChange({ ...sepa, iban: normalized });
        const invalid = !!normalized && !validateIban(normalized);
        if (invalid) {
            setIbanError(t("register.errorIbanFormat"));
            onIbanError?.(true);
        } else {
            setIbanError(null);
            onIbanError?.(false);
        }
    };

    return (
        <Section title={t("settings.sepaPayment")} description={description}>
            <SettingRow title={t("settings.sepaAccountHolder")}>
                <TextInput
                    value={sepa.accountHolder}
                    onChange={(v) => onChange({ ...sepa, accountHolder: v })}
                    placeholder={t("settings.sepaAccountHolderPlaceholder")}
                />
            </SettingRow>
            <SettingRow title={t("settings.iban")}>
                <TextInput
                    value={sepa.iban}
                    onChange={(v) => { onChange({ ...sepa, iban: v }); setIbanError(null); }}
                    onBlur={handleIbanBlur}
                    placeholder="DE00 0000 0000 0000 0000 00"
                />
            </SettingRow>
            {ibanError && (
                <div className="settings-info-banner settings-info-banner--error">
                    <TbAlertCircle />
                    {ibanError}
                </div>
            )}
            <SettingRow title={t("settings.bic")}>
                <TextInput
                    value={sepa.bic}
                    onChange={(v) => onChange({ ...sepa, bic: v.toUpperCase() })}
                    placeholder="DEUTDEFFXXX"
                />
            </SettingRow>
        </Section>
    );
}
